import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Api from "services/api";
import toast from "react-hot-toast";
import { ListLayout } from "components/common/ListLayout";
import TableHeaderItem from "components/common/ListLayout/TableHeaderItem";
import { userSelector } from "slices/userSlice";
import { usePermissions } from "layouts/Admin";
import { Controller, useForm } from "react-hook-form";
import moment from "moment";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import { AsyncPaginate } from "react-select-async-paginate";
import { selectCustomStyles } from "components/common/Select";
import ErrorMessage from "components/common/ErrorMessage";
import { fetchInterpreters } from "slices/interpreterSlice";
import {
  fetchStaffTimesheets,
  fetchStaff,
  fetchModalStaffTimesheets,
  setLoader,
} from "slices/timesheetSlice";
import ExcelJS from "exceljs";
import { saveAs } from 'file-saver';
import { Modal, ModalBody, ModalHeader } from "reactstrap";
const apiService = new Api();

function Timesheet() {
  const dispatch = useDispatch();
  const { checkPermission } = usePermissions();
  const { user } = useSelector(userSelector);
  const userToken = user.token;
  const userRole = user.role;
  const [hasFiltered, setHasFiltered] = useState(false);
  const [paginationParams, setPaginationParams] = useState({});
  const filtersRef = useRef({});
  const {
    timesheets,
    modalTimesheets,
    loader,
    total,
    last_page: totalPages,
    current_page,
    per_page: pageLength,
    modal_total: modalTotal,
    modal_last_page: modalTotalPages,
    modal_current_page: modalCurrentPageFromStore,
    modal_per_page: modalPerPage,
  } = useSelector((state) => state.timesheet || {});
  // Modal State
  const [viewModal, setViewModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalLogs, setModalLogs] = useState([]);
  const [modalCurrentPage, setModalCurrentPage] = useState(1);
  const [modalLoader, setModalLoader] = useState(false);
  const [modalSearchTerm, setModalSearchTerm] = useState("");
  const [modalPageLength, setModalPageLength] = useState(10);
  const [modalPaginationParams, setModalPaginationParams] = useState({});

  const [currentPage, setCurrentPage] = useState(1);
  const [mainPageLength, setMainPageLength] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    sortBy: "date",
    sortDirection: "desc",
  });

  // Filters state
  const [interpreterState, setInterpreterState] = useState([]);
  const defaultValues = {
    start_date: "",
    end_date: "",
    interpreter_ids: [],
  };

  useEffect(() => {
    dispatch(setLoader(false));

    return () => {
      dispatch(setLoader(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (viewModal) return;
    if (!hasFiltered) {
      setMainPageLength(0);
      return;
    }
    setMainPageLength(pageLength || 10);
  }, [pageLength, viewModal, hasFiltered]);

  const toggleViewModal = () => {
    setViewModal(!viewModal);
    if (viewModal) {
      setSelectedRow(null);
      setModalLogs([]);
      setModalSearchTerm("");
      setModalCurrentPage(1);
      setModalPageLength(10);
      setModalPaginationParams({});
    }
  };
  
  const handleView = (row) => {
    const modalParams = {
      interpreter_ids: [row.staff_id || row.id],
      start_date: row.from_date,
      end_date: row.to_date,
      search: "",
      pageLength: 10,
    };

    setSelectedRow(row);
    setModalCurrentPage(1);
    setModalSearchTerm("");
    setModalPageLength(10);
    setModalPaginationParams(modalParams);
    setViewModal(true);
    dispatch(fetchModalStaffTimesheets(1, modalParams));
  };
  
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({ defaultValues });

  console.log('>>> Redux timesheet state:', useSelector((state) => state.timesheet));
  console.log('>>> timesheets (local variable):', timesheets);


  //#region FILTER HANDLERS

  const handleResetFields = () => {
    reset(defaultValues);
    setHasFiltered(false);
    setPaginationParams({});
    filtersRef.current = {};
    setInterpreterState([]);
    dispatch(fetchStaffTimesheets(1, defaultValues));
  };

  const onSubmit = (data) => {
    setHasFiltered(true);
    setPaginationParams(data);
    filtersRef.current = data;
    dispatch(fetchStaffTimesheets(1, data));
  };

  const filteredInterpreterLoadOptions = async (
    inputValue,
    loadedOptions,
    { page }
  ) => {
    const result = await dispatch(
      fetchStaff(
        page,
        {
          pageLength: 20,
          sortBy: "id",
          sortDirection: "asc",
          search: inputValue,
        },
        true,
        true
      )
    );

    return {
      options: result.data.map((item) => ({
        // prefer user_id, then user.id, then item.id
        value: item.user_id ?? item.user?.id ?? item.id,
        label: item.user?.name ?? item.name ?? `Interpreter ${item.id}`,
      })),
      hasMore: page !== result?.last_page,
      additional: { page: page + 1 },
    };
  };

  //#endregion

  //#region TABLE HEAD

  const renderTableHeader = React.useCallback(() => {
    const callBack = (dir, sortBy) => {
      setSortConfig({ sortBy, sortDirection: dir });
      dispatch(
        fetchStaffTimesheets(currentPage, {
          sortBy,
          sortDirection: dir,
          search: searchTerm,
        })
      );
    };

    return (
      <tr>
        <th className="col-1">#</th>
        {userRole === 'admin' && (
        <>
        <th className="col-1">From Date</th>
        <th className="col-1">To Date</th>
        <th className="col-2">Staff Name</th>
        <th className="col-2">Action</th>
        </>
        )}
        {userRole !== 'admin' && (
        <>
        <th className="col-2">
          <TableHeaderItem
            title="Date"
            sortBy="date"
            sortDirection={sortConfig.sortDirection}
            callBack={callBack}
          />
        </th>
        <th className="col">Check In/Out</th>
        <th className="col-2">Daily Duration</th>
        </>
        )}
      </tr>
    );
  }, [sortConfig, currentPage, searchTerm]);

  const [timesheetLoader, setTimesheetLoader] = useState(false)


  const handleDownloadTimesheet = async () => {
    if (!paginationParams?.start_date || !paginationParams?.end_date) {
        toast.error("From date and To date is required.");
        return;
    }
    setTimesheetLoader(true);
    try {
      // Step 1️⃣: Setup query params
      const params = {
        ...paginationParams, // includes start_date, end_date etc.
        sortBy: sortConfig?.sortBy,
        sortDirection: sortConfig?.sortDirection,
        search: searchTerm,
      };

      console.log("🚦 Export (Staff) - fetching all pages with params:", params);

      let page = 1;
      let allData = [];
      let lastPage = 1;

      // Step 2️⃣: Fetch all pages from backend
      do {
        const res = await Api.fetchStaffTimesheets(page, params); // 👈 use correct staff API
        const response = res?.data;

        if (!response || !response.data) break;

        allData = [...allData, ...response.data];
        lastPage = response.pagination?.last_page || 1;

        console.log(`✅ Staff Page ${page} fetched: ${response.data.length} items`);
        page++;
      } while (page <= lastPage);

      console.log("🚦 All staff pages combined:", allData.length);

      if (allData.length === 0) {
        toast.error("No data available to download.");
        return;
      }

      // Step 3️⃣: Create Excel Workbook
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Timesheet");

      // ✅ Interpreter Name Row
      const staffName = allData[0]?.staff_name || "Staff";
      const title = `Timesheet for ${staffName}`;
      const titleRow = worksheet.addRow([title]);

      // Merge across 4 columns
      worksheet.mergeCells("A1:H1");

      // Style the title
      titleRow.getCell(1).font = { name: "Calibri", size: 14, bold: true, color: { argb: "6C889C" } };
      titleRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
      titleRow.height = 25;

      // ✅ Add header row
      const headers = ["#", "From Date", "To Date", "Staff Name", "Position", "Total Duration" , "Rate" , "Total Amount"];
      const headerRow = worksheet.addRow(headers);

      headerRow.eachCell((cell) => {
        cell.font = { name: "Calibri", size: 12, bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "6F8DFF" },
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      worksheet.views = [{ state: "frozen", ySplit: 2 }];
      headerRow.height = 20;

      // ✅ Add data rows
      allData.forEach((item, index) => {
        worksheet.addRow([
          index + 1,
          moment(item.start_date).format("YYYY-MM-DD"),
          moment(item.end_date).format("YYYY-MM-DD"),
          item.staff_name || "N/A",
          item.position || "N/A",
          item.total_duration || "0h",
          item.rate || "0.00",
          (item.total_minutes / 60) * item.rate || "0.00"
        ]);
      });

      // ✅ Auto column width + fixed width for Check In/Out
      // worksheet.columns.forEach((col, index) => {
      //   if (index === 2) {
      //     col.width = 100; // ≈160px
      //   } else {
      //     let max = 0;
      //     col.eachCell({ includeEmpty: true }, (cell) => {
      //       const val = cell.value ? cell.value.toString() : "";
      //       max = Math.max(max, val.length);
      //     });
      //     col.width = max + 3;
      //   }
      // });

      // ✅ Calculate total duration
      // let totalMinutes = 0;
      // allData.forEach((item) => {
      //   if (item.daily_duration) {
      //     const str = item.daily_duration.toString().trim().toLowerCase();
      //     let hours = 0,
      //       minutes = 0;

      //     if (str.includes("h")) {
      //       const hMatch = str.match(/(\d+)\s*h/);
      //       const mMatch = str.match(/(\d+)\s*m/);
      //       hours = hMatch ? parseInt(hMatch[1]) : 0;
      //       minutes = mMatch ? parseInt(mMatch[1]) : 0;
      //     } else if (str.includes(":")) {
      //       const [h, m] = str.split(":");
      //       hours = parseInt(h);
      //       minutes = parseInt(m);
      //     }

      //     totalMinutes += hours * 60 + minutes;
      //   }
      // });

      // const totalHours = Math.floor(totalMinutes / 60);
      // const remainingMinutes = totalMinutes % 60;
      // const totalFormatted = `${totalHours}h ${remainingMinutes}m`;
      // const totalDays = allData.length;

      // const daysRow = worksheet.addRow(["", "", "Total Days", totalDays]);
      // daysRow.font = { bold: true };
      // // ✅ Total row
      // const totalRow = worksheet.addRow(["", "", "Total Duration:", totalFormatted]);

      // [totalRow, daysRow].forEach((row) => {
      //   row.eachCell((cell, colNumber) => {
      //     cell.font = { bold: true };
      //     cell.border = {
      //       top: { style: "thin" },
      //       left: { style: "thin" },
      //       bottom: { style: "thin" },
      //       right: { style: "thin" },
      //     };
      //     if (colNumber === 3) cell.alignment = { horizontal: "right" };
      //     if (colNumber === 4)
      //       cell.fill = {
      //         type: "pattern",
      //         pattern: "solid",
      //         fgColor: { argb: "D9E1F2" },
      //       };
      //   });
      // });

      // ✅ Export the file
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `timesheet_${moment().format("YYYYMMDD_HHmmss")}.xlsx`
      );

      toast.success("Timesheet downloaded successfully!");
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to download timesheet. See console for details.");
    }finally{
      setTimesheetLoader(false);
    }
  };



  //#endregion

  //#region TABLE BODY

  const renderTableContent = React.useCallback(() => {
    if (!hasFiltered || !timesheets?.length) {
      // ✅ Return empty fragment so table stays but shows no rows
      return <></>;
    }

    return timesheets.map((item, index) => (
      <TimesheetItems
        key={item.id}
        data={item}
        index={index}
        current_page={current_page}
        pageLength={pageLength}
        userRole={userRole}
        handleView={handleView}
      />
    ));
  }, [timesheets, hasFiltered]);


  const renderModalTableHeader = React.useCallback(() => {
    return (
      <tr>
        <th className="col-1">#</th>
        <th className="col-3">Date</th>
        <th className="col-4">Check In/Out</th>
        <th className="col-2">Daily Duration</th>
      </tr>
    );
  }, []);

  const renderModalTableContent = React.useCallback(() => {
    if (!modalTimesheets?.length) return <></>;

    return modalTimesheets.map((item, index) => (
      <tr key={index}>
        <td>{index + 1 + (modalCurrentPage - 1) * modalPageLength}</td>
        <td>{moment(item.date).format("YYYY-MM-DD")}</td>
        <td>{item.pairs || 'N/A'}</td>
        <td>{item.daily_duration}</td>
      </tr>
    ));
  }, [modalTimesheets, modalCurrentPage, modalPageLength]);

  useEffect(() => {
    if (!viewModal) return;

    setModalCurrentPage(modalCurrentPageFromStore || 1);
    setModalPageLength(modalPaginationParams.pageLength || modalPerPage || 10);
  }, [viewModal, modalTimesheets, modalCurrentPageFromStore, modalPerPage, modalPaginationParams]);

  const fetchModalList = React.useCallback(
    (page, extraParams = {}, disableLoader = false) =>
      async (dispatch) => {
        if (!selectedRow) return;

        const params = {
          interpreter_ids: [selectedRow.staff_id || selectedRow.id],
          start_date: selectedRow.from_date,
          end_date: selectedRow.to_date,
          ...modalPaginationParams,
          ...extraParams,
        };

        setModalCurrentPage(page);

        if (Object.prototype.hasOwnProperty.call(extraParams, "search")) {
          setModalSearchTerm(extraParams.search || "");
        }

        if (extraParams.pageLength) {
          setModalPageLength(extraParams.pageLength);
        }

        setModalPaginationParams(params);
        await dispatch(fetchModalStaffTimesheets(page, params, disableLoader));
      },
    [selectedRow, modalPaginationParams]
  );

  const handleDownloadModalTimesheet = async () => {
    try {
      if (!modalTimesheets?.length) {
        toast.error("No data available to download.");
        return;
      }

      // ✅ Create Excel Workbook
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Timesheet");

      // ✅ Staff Name Row
      const staffName = selectedRow?.staff_name || "Staff";
      const title = `Timesheet for ${staffName}`;
      const titleRow = worksheet.addRow([title]);

      // Merge across 4 columns
      worksheet.mergeCells("A1:D1");

      // Style the title
      titleRow.getCell(1).font = { name: "Calibri", size: 14, bold: true, color: { argb: "6C889C" } };
      titleRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
      titleRow.height = 25;

      // ✅ Add header row
      const headers = ["#", "Date", "Check In/Out", "Daily Duration"];
      const headerRow = worksheet.addRow(headers);

      headerRow.eachCell((cell) => {
        cell.font = { name: "Calibri", size: 12, bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "6F8DFF" },
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      worksheet.views = [{ state: "frozen", ySplit: 2 }];
      headerRow.height = 20;

      // ✅ Add data rows
      modalTimesheets.forEach((item, index) => {
        worksheet.addRow([
          index + 1,
          moment(item.date).format("YYYY-MM-DD"),
          item.pairs || "N/A",
          item.daily_duration || "0h",
        ]);
      });

      // ✅ Auto column width + fixed width for Check In/Out
      worksheet.columns.forEach((col, index) => {
        if (index === 2) {
          col.width = 100; // ≈160px
        } else {
          let max = 0;
          col.eachCell({ includeEmpty: true }, (cell) => {
            const val = cell.value ? cell.value.toString() : "";
            max = Math.max(max, val.length);
          });
          col.width = max + 3;
        }
      });

      // ✅ Calculate total duration
      let totalMinutes = 0;
      modalTimesheets.forEach((item) => {
        if (item.daily_duration) {
          const str = item.daily_duration.toString().trim().toLowerCase();
          let hours = 0,
            minutes = 0;

          if (str.includes("h")) {
            const hMatch = str.match(/(\d+)\s*h/);
            const mMatch = str.match(/(\d+)\s*m/);
            hours = hMatch ? parseInt(hMatch[1]) : 0;
            minutes = mMatch ? parseInt(mMatch[1]) : 0;
          } else if (str.includes(":")) {
            const [h, m] = str.split(":");
            hours = parseInt(h);
            minutes = parseInt(m);
          }

          totalMinutes += hours * 60 + minutes;
        }
      });

      const totalHours = Math.floor(totalMinutes / 60);
      const remainingMinutes = totalMinutes % 60;
      const totalFormatted = `${totalHours}h ${remainingMinutes}m`;
      const totalDays = modalTimesheets.length;

      const daysRow = worksheet.addRow(["", "", "Total Days", totalDays]);
      daysRow.font = { bold: true };
      // ✅ Total row
      const totalRow = worksheet.addRow(["", "", "Total Duration:", totalFormatted]);

      [totalRow, daysRow].forEach((row) => {
        row.eachCell((cell, colNumber) => {
          cell.font = { bold: true };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          if (colNumber === 3) cell.alignment = { horizontal: "right" };
          if (colNumber === 4)
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "D9E1F2" },
            };
        });
      });

      // ✅ Export the file
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `timesheet_${staffName}_${moment().format("YYYYMMDD_HHmmss")}.xlsx`
      );

      toast.success("Timesheet downloaded successfully!");
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to download timesheet. See console for details.");
    }
  };

  //#endregion
  return (
    <>
      {/* ---------------- FILTER FORM ---------------- */}
      <div className="content" style={{ minHeight: "auto" }}>
        <div className="row_main_head">
          <h2 className="main_heading">{userRole == 'admin' ? 'Staff Timesheet' : 'Timesheet'}</h2>
        </div>
        <div className="card_table">
          <form className="invoicing_form" onSubmit={handleSubmit(onSubmit)}>
            <div className="billings row_Invoice">
              {/* From Date */}
              <div className="row-primary">
                <h2>From&nbsp;Date&nbsp;*</h2>
                <Controller
                  name="start_date"
                  rules={{ required: "This field is required" }}
                  control={control}
                  render={({ field }) => (
                    <div className="input-image-wrapper d-flex flex-column w-100 align-items-start">
                      <div className="input-image">
                        <input
                          type="date"
                          {...field}
                          onChange={(e) =>
                            field.onChange(
                              moment(new Date(e.target.value)).format("YYYY-MM-DD")
                            )
                          }
                        />
                        <img
                          src="/images/celender.png"
                          alt="calendar Icon"
                          className="custom-icon"
                        />
                      </div>
                      {/* ✅ Error Message */}
                      {errors?.start_date?.message && (
                        <div style={{ marginBottom: 10 }}>
                          <ErrorMessage text={errors?.start_date?.message} />
                        </div>
                      )}
                    </div>
                  )}
                />
              </div>

              {/* To Date */}
              <div className="row-primary">
                <h2>To Date *</h2>
                <Controller
                  name="end_date"
                  rules={{ required: "This field is required" }}
                  control={control}
                  render={({ field }) => (

                    <div className="input-image-wrapper d-flex flex-column w-100 align-items-start">
                      <div className="input-image">
                        <input
                          type="date"
                          {...field}
                          onChange={(e) =>
                            field.onChange(
                              moment(new Date(e.target.value)).format("YYYY-MM-DD")
                            )
                          }
                        />
                        <img
                          src="/images/celender.png"
                          alt="calendar Icon"
                          className="custom-icon"
                        />
                      </div>
                      {/* ✅ Error Message */}
                      {errors?.end_date?.message && (
                        <div style={{ marginBottom: 10 }}>
                          <ErrorMessage text={errors?.end_date?.message} />
                        </div>
                      )}
                    </div>
                  )}
                />
              </div>

              {/* Interpreters */}
              {(userRole === "admin") && (
              <div className="row-primary">
                <h2>Staff</h2>
                <Controller
                  name="interpreter_ids"
                  // rules={{ required: "This field is required" }}
                  control={control}
                  render={({ field }) => (
                    <div style={{ width: "100%" }}>
                      <AsyncPaginate
                        menuPlacement="auto"
                        isMulti
                        styles={{
                          ...selectCustomStyles,
                        }}
                        value={interpreterState || []}
                        loadOptions={filteredInterpreterLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => {
                          setInterpreterState(obj);
                          field.onChange(obj.map((opt) => opt.value));
                        }}
                        additional={{
                          page: 1,
                        }}
                      />
                      {errors?.interpreter_ids?.message && (
                        <div style={{ marginBottom: 10 }}>
                          <ErrorMessage text={errors?.interpreter_ids?.message} />
                        </div>
                      )}
                    </div>
                  )}
                />
              </div>
              )}
            </div>

            <div className="row-primary row_10 mb-0 p-3">
              <SubmitButton
                handleSubmit={handleSubmit(onSubmit)}
                btnText="Search"
                className={"green"}
              />
              <SubmitButton
                buttonType="button"
                className={"lightBlue"}
                btnText="Reset"
                handleSubmit={handleResetFields}
              />
            </div>
          </form>
        </div>
      </div>

      {/* ---------------- TABLE SECTION ---------------- */}
      <ListLayout
        buttonTitle='Downlaod Timesheet'
        time={false}
        // addButtonDisabled={true}
        renderTableHeader={renderTableHeader}
        renderTableContent={renderTableContent}
        totalPages={hasFiltered ? totalPages : 0}
        fetchList={(page, extraParams = {}, disableLoader) => {
          const currentFilters = {
            start_date: filtersRef.current.start_date || "",
            end_date: filtersRef.current.end_date || "",
            interpreter_ids: filtersRef.current.interpreter_ids || [],
          };

          const params = {
            ...extraParams,
            ...currentFilters,
          };

          console.log("🧠 Final Params Sent:", params);
          return fetchStaffTimesheets(page, params, disableLoader);
        }}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        loader={loader || timesheetLoader}
        noOfItems={hasFiltered ? total : 0}
        containerStyle={{ marginTop: "0" }}
        searchPlaceholder="Search Date..."
        handleAddPopUp={handleDownloadTimesheet}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        disableBootstrap={true}
        paginationParams={paginationParams}
        defaultPageLength={mainPageLength}
        sortConfig={sortConfig}
      />

      {/* ---------------- DETAIL MODAL ---------------- */}
      <Modal isOpen={viewModal} toggle={toggleViewModal} size="xl" centered>
        <ModalHeader toggle={toggleViewModal}>
          Timesheet Details - {selectedRow?.staff_name}
        </ModalHeader>
        <ModalBody>
          {selectedRow && (
            <ListLayout
              buttonTitle={'Download Timesheet'}
              handleAddPopUp={handleDownloadModalTimesheet}
              time={false}
              renderTableHeader={renderModalTableHeader}
              renderTableContent={renderModalTableContent}
              totalPages={modalTotalPages}
              fetchList={fetchModalList}
              currentPage={modalCurrentPage}
              setCurrentPage={setModalCurrentPage}
              loader={modalLoader}
              noOfItems={modalTotal}
              containerStyle={{ marginTop: "0", boxShadow: "none" }}
              searchPlaceholder="Search Date..."
              searchTerm={modalSearchTerm}
              setSearchTerm={setModalSearchTerm}
              disableBootstrap={true}
              paginationParams={modalPaginationParams}
              defaultPageLength={modalPageLength}
            />
          )}
        </ModalBody>
      </Modal>
    </>
  );
}

// ✅ Table Item Component
const TimesheetItems = ({ data, index, current_page, pageLength, userRole, handleView}) => {
  const serial = useMemo(() => {
    return index + 1 + (current_page - 1) * pageLength;
  }, [current_page, pageLength]);


  return (
    <tr>
      <td>{serial}</td>
      {userRole === 'admin' && (
      <>
      <td>{moment(data?.from_date).format("YYYY-MM-DD")}</td>
      <td>{moment(data?.to_date).format("YYYY-MM-DD")}</td>
      <td>{data?.staff_name}</td>
      <td>
        <button
          className="main_button green"
          type="button"
          onClick={() => handleView(data)}
        >
          View
        </button>
      </td>
      </>
      )}
      {userRole !== 'admin' && (
      <>
      <td>{moment(data?.date).format("YYYY-MM-DD")}</td>
      <td style={{
        width: '10px !important',
        overflowX: 'auto',
        whiteSpace: 'nowrap',
        maxWidth: '100px',
      }}>{data?.pairs || "N/A"}</td>
      <td>{data?.daily_duration || "0h"}</td>
      </>
      )}
    </tr>
  );
};

export default Timesheet;
